import type { ModelLeadFilterData } from '@app/api/Leads/types';
import type { CheckboxFilterOptions } from '@app/components/CheckboxFilter/types';

import { LEADS_REGISTRY_NO_RESPONSIBLE_OPTION_ID } from '../constants';

import type { LeadsRegistryCheckboxOptions } from '../types';

interface MapLeadsFilterDataParams {
  userId: number;
  userName: string;
}

const EMPTY_CHECKBOX_OPTIONS: LeadsRegistryCheckboxOptions = {
  branches: [],
  channels: [],
  integrations: [],
  users: [],
  statuses: [],
  crmTaskStatuses: [],
};

// Maps the `GetLeadsFilterData` dictionary into checkbox options for the
// Leads registry filters. Mirrors the legacy registry option mapping:
// numeric dictionaries (branches/integrations/users) use the numeric id as a
// string; string dictionaries (channels/statuses/tasks) use `IdS`. The
// responsible filter appends the synthetic "no responsible" and "me" options.
export const mapLeadsFilterDataToCheckboxOptions = (
  filterData: ModelLeadFilterData | undefined,
  { userId, userName }: MapLeadsFilterDataParams
): LeadsRegistryCheckboxOptions => {
  if (!filterData) {
    return EMPTY_CHECKBOX_OPTIONS;
  }

  const { Branches, Channels, Integrations, Users, Statuses, CrmTaskStatuses } =
    filterData;

  const usersWithoutCurrent = (Users ?? []).filter(({ Id }) => Id !== userId);

  const users: CheckboxFilterOptions[] = [
    ...usersWithoutCurrent.map(({ Id, Name }) => ({
      id: String(Id),
      label: Name ?? '',
    })),
    {
      id: LEADS_REGISTRY_NO_RESPONSIBLE_OPTION_ID,
      label: 'Без ответственного',
      bold: true,
    },
    {
      id: String(userId),
      label: `Я (${userName})`,
      bold: true,
    },
  ];

  return {
    branches: (Branches ?? []).map(({ Id, Name }) => ({
      id: String(Id),
      label: Name ?? '',
    })),
    channels: (Channels ?? []).map(({ IdS, Name }) => ({
      id: IdS ?? '',
      label: Name ?? '',
      iconId: IdS ?? undefined,
    })),
    integrations: (Integrations ?? []).map(({ Id, IdS, Name }) => ({
      id: String(Id),
      label: Name ?? '',
      iconId: IdS ?? undefined,
    })),
    users,
    statuses: (Statuses ?? []).map(({ IdS, Name }) => ({
      id: IdS ?? '',
      label: Name ?? '',
    })),
    crmTaskStatuses: (CrmTaskStatuses ?? []).map(({ IdS, Name }) => ({
      id: IdS ?? '',
      label: Name ?? '',
    })),
  };
};
